import React, { useContext, useCallback, useState, Suspense } from 'react';
import { useIntegration, useDecision, ConsentManager } from '@consent-manager/core';
import { IoShieldCheckmark } from '@react-icons/all-files/io5/IoShieldCheckmark';
import clsx from 'clsx';

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}
function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];
  return arr2;
}
function _createForOfIteratorHelperLoose(o, allowArrayLike) {
  var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"];
  if (it) return (it = it.call(o)).next.bind(it);
  if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") {
    if (it) o = it;
    var i = 0;
    return function () {
      if (i >= o.length) return {
        done: true
      };
      return {
        done: false,
        value: o[i++]
      };
    };
  }
  throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var defaultStyles = {"icon":"index-module_icon__Q3LZr","wrapper":"index-module_wrapper__811ne","pane":"index-module_pane__RZjoO","slide":"index-module_slide__N6qDT","backdrop":"index-module_backdrop__Pb6RU","form":"index-module_form__B77D-","formContent":"index-module_formContent__5J4ND","formTitle":"index-module_formTitle__ItJ8n","formIcon":"index-module_formIcon__OcQZg","formControls":"index-module_formControls__e4Y7b","formIntegrations":"index-module_formIntegrations__cQNTj","integrationField":"index-module_integrationField__ZCO3X","formIntegrationsList":"index-module_formIntegrationsList__vKGfz","integrationFieldTrigger":"index-module_integrationFieldTrigger__ucEfs","integrationFieldDetails":"index-module_integrationFieldDetails__sER9u","integrationFieldCategory":"index-module_integrationFieldCategory__-kq7L","integrationFieldCompany":"index-module_integrationFieldCompany__ye74E","integrationLabel":"index-module_integrationLabel__UI4Gj","integrationLabelIcon":"index-module_integrationLabelIcon__vSDjW","integrationLabelDescription":"index-module_integrationLabelDescription__FAZiP","integrationPrivacyPolicyLink":"index-module_integrationPrivacyPolicyLink__qKz4i","toggleButton":"index-module_toggleButton__J8ksc","toggleButtonContent":"index-module_toggleButtonContent__eE3Ea","toggleButtonIcon":"index-module_toggleButtonIcon__OCOi-","introduction":"index-module_introduction__LHMxt","introductionShape":"index-module_introductionShape__AuWnu","introductionContent":"index-module_introductionContent__k9lLi","introductionTitle":"index-module_introductionTitle__LMmn4","introductionDescription":"index-module_introductionDescription__kNITz","introductionControls":"index-module_introductionControls__JKsI6","buttonReset":"index-module_buttonReset__Qm7-f","button":"index-module_button__fOW5S","buttonClose":"index-module_buttonClose__iAaWb","formButtonClose":"index-module_formButtonClose__5ycuG","buttonCloseIcon":"index-module_buttonCloseIcon__6umRX","fallbackComponent":"index-module_fallbackComponent__8WdkG","fallbackComponentContent":"index-module_fallbackComponentContent__Kz3H9","fallbackComponentTitle":"index-module_fallbackComponentTitle__EoHfW","fallbackComponentIcon":"index-module_fallbackComponentIcon__f57ws","fallbackComponentControls":"index-module_fallbackComponentControls__Oi3P5","switchLabel":"index-module_switchLabel__0e-7a","switchWrapper":"index-module_switchWrapper__7Aq-u","switchSlide":"index-module_switchSlide__iGpps","switchSlideEnabled":"index-module_switchSlideEnabled__2aerF","switchNodge":"index-module_switchNodge__nL4HZ","switchNodgeEnabled":"index-module_switchNodgeEnabled__tESCk","switchContent":"index-module_switchContent__wuszz","switchInput":"index-module_switchInput__K-LG-"};

var useDefaultButton = function useDefaultButton(styles) {
  return React.useMemo(function () {
    var DefaultButton = function DefaultButton(props) {
      return React.createElement("button", Object.assign({}, props, {
        className: clsx(props.className, styles.buttonReset, styles.button)
      }));
    };
    return DefaultButton;
  }, [styles]);
};

var ConsentManagerDefaultInterfaceContext = /*#__PURE__*/React.createContext({
  formVisible: false,
  setFormVisible: function setFormVisible() {},
  messages: {
    'consent-manager.integration.default.company': function consentManagerIntegrationDefaultCompany() {
      return null;
    },
    'consent-manager.fallback.default.description': function consentManagerFallbackDefaultDescription() {
      return null;
    },
    'consent-manager.integration.default.title': function consentManagerIntegrationDefaultTitle() {
      return null;
    },
    'consent-manager.integration.default.privacy-policy': function consentManagerIntegrationDefaultPrivacyPolicy() {
      return null;
    },
    'consent-manager.integration.default.category': function consentManagerIntegrationDefaultCategory() {
      return null;
    },
    'consent-manager.integration.default.description': function consentManagerIntegrationDefaultDescription() {
      return null;
    },
    'consent-manager.fallback.default.enable': function consentManagerFallbackDefaultEnable() {
      return null;
    }
  }
});

var Trans = function Trans(_ref) {
  var id = _ref.id,
    fallbackId = _ref.fallbackId,
    render = _ref.render,
    props = _ref.props;
  var _useContext = useContext(ConsentManagerDefaultInterfaceContext),
    messages = _useContext.messages;
  var Result = messages[id] || fallbackId && messages[fallbackId] || id;
  if (render && typeof Result === 'string') {
    return render({
      message: Result
    });
  }
  if (typeof Result === 'function') {
    return React.createElement(Result, Object.assign({}, props));
  }
  return React.createElement(React.Fragment, null, Result);
};

var IntegrationLabel = function IntegrationLabel(_ref) {
  var styles = _ref.styles,
    _ref$integration = _ref.integration,
    contrastColor = _ref$integration.contrastColor,
    color = _ref$integration.color,
    id = _ref$integration.id,
    title = _ref$integration.title,
    Icon = _ref$integration.Icon;
  return React.createElement("span", {
    className: clsx(styles.integrationLabel),
    style: {
      color: contrastColor,
      backgroundColor: color
    }
  }, React.createElement(Icon, {
    className: clsx(styles.integrationLabelIcon)
  }), React.createElement("span", {
    className: clsx(styles.integrationLabelTitle)
  }, React.createElement(Trans, {
    id: "consent-manager.integration." + id + ".title",
    fallbackId: "consent-manager.integration.default.title",
    props: {
      title: title
    }
  })));
};

var FallbackComponent = function FallbackComponent(_ref) {
  var integrationId = _ref.integrationId,
    Button = _ref.Button,
    styles = _ref.styles,
    ToggleIcon = _ref.ToggleIcon;
  var _useContext = useContext(ConsentManagerDefaultInterfaceContext),
    setFormVisible = _useContext.setFormVisible;
  var integration = useIntegration(integrationId);
  var _useDecision = useDecision(integrationId),
    setDecision = _useDecision[1];
  var enableIntegration = useCallback(function () {
    setDecision(true);
  }, [setDecision]);
  if (!integration) {
    throw new Error("Integration " + integrationId + " could not be found.");
  }
  var category = integration.category,
    title = integration.title;
  return React.createElement("section", {
    className: clsx(styles.fallbackComponent)
  }, React.createElement("div", {
    className: clsx(styles.fallbackComponentContent)
  }, React.createElement("h1", {
    className: clsx(styles.fallbackComponentTitle)
  }, React.createElement(ToggleIcon, {
    className: clsx(styles.icon, styles.fallbackComponentIcon)
  }), React.createElement(Trans, {
    id: "consent-manager.fallback." + integrationId + ".title",
    fallbackId: "consent-manager.fallback.default.title"
  })), React.createElement(Trans, {
    id: "consent-manager.fallback." + integrationId + ".description",
    fallbackId: "consent-manager.fallback.default.description",
    props: {
      IntegrationLabel: function IntegrationLabel$1() {
        return React.createElement(IntegrationLabel, {
          styles: styles,
          integration: integration
        });
      },
      category: category,
      title: title
    }
  }), React.createElement("div", {
    className: clsx(styles.fallbackComponentControls)
  }, React.createElement(Button, {
    onClick: function onClick() {
      return setFormVisible(true);
    }
  }, React.createElement(Trans, {
    id: "consent-manager.fallback." + integrationId + ".learn-more",
    fallbackId: "consent-manager.fallback.default.learn-more"
  })), React.createElement(Button, {
    "data-button-style": "primary",
    onClick: function onClick() {
      return enableIntegration();
    }
  }, React.createElement(Trans, {
    id: "consent-manager.fallback." + integrationId + ".enable",
    fallbackId: "consent-manager.fallback.default.enable",
    props: {
      category: category,
      title: title
    }
  })))));
};

var defaultMessages = {
  // General
  'consent-manager.close': 'close',
  // Intro
  'consent-manager.introduction.title': 'Data protection enabled',
  'consent-manager.introduction.description': 'Some Website features are disabled to protect your privacy.',
  'consent-manager.introduction.learn-more': 'Learn more',
  'consent-manager.introduction.enable-all': 'Enable all features',
  // Form
  'consent-manager.form.headline': 'Website Features and Cookies',
  'consent-manager.form.description': function consentManagerFormDescription() {
    return React.createElement(React.Fragment, null, React.createElement("p", null, "By default third party features are disabled to protect your privacy."), React.createElement("p", null, "To view third-party content, you first have to accept their specific terms and conditions. This includes their cookie policies, which can change anytime and which we have no control over.", React.createElement("br", null), "But if you do not view this content, no third-party cookies are installed on your device."), React.createElement("p", null, "By activating the features you agree to the providers' terms of use and their cookie policy. You can opt out at any time."));
  },
  'consent-manager.form.reset': 'reset defaults',
  'consent-manager.form.enable-all': 'enable all features',
  'consent-manager.form.disable-all': 'disable all features',
  'consent-manager.form.save': 'save and close',
  // Fallback component
  'consent-manager.fallback.default.title': 'Recommended external content',
  'consent-manager.fallback.default.description': function consentManagerFallbackDefaultDescription(_ref) {
    var IntegrationLabel = _ref.IntegrationLabel;
    return React.createElement(React.Fragment, null, React.createElement("p", null, "This feature contains content by ", React.createElement(IntegrationLabel, null)), React.createElement("p", null, "To view this third-party content, you first have to accept their specific terms and conditions."), React.createElement("p", null, "This includes their cookie policies, which we have no control over."));
  },
  'consent-manager.fallback.default.enable': function consentManagerFallbackDefaultEnable(_ref2) {
    var category = _ref2.category,
      title = _ref2.title;
    return React.createElement(React.Fragment, null, "Enable ", category, " by ", title);
  },
  'consent-manager.fallback.default.learn-more': 'Learn more',
  // Integration Default
  'consent-manager.integration.default.company': function consentManagerIntegrationDefaultCompany(_ref3) {
    var IntegrationLabel = _ref3.IntegrationLabel;
    return React.createElement(React.Fragment, null, "by ", React.createElement(IntegrationLabel, null));
  },
  'consent-manager.integration.default.category': function consentManagerIntegrationDefaultCategory(_ref4) {
    var category = _ref4.category;
    return React.createElement(React.Fragment, null, category);
  },
  'consent-manager.integration.default.title': function consentManagerIntegrationDefaultTitle(_ref5) {
    var title = _ref5.title;
    return React.createElement(React.Fragment, null, title);
  },
  'consent-manager.integration.default.description': function consentManagerIntegrationDefaultDescription(_ref6) {
    var description = _ref6.description,
      PrivacyPolicyLink = _ref6.PrivacyPolicyLink;
    return React.createElement("p", null, description, React.createElement("br", null), React.createElement(PrivacyPolicyLink, null));
  },
  'consent-manager.integration.default.privacy-policy': function consentManagerIntegrationDefaultPrivacyPolicy(_ref7) {
    var Link = _ref7.Link,
      title = _ref7.title;
    return React.createElement(Link, null, "Privacy Policy by ", title);
  }
};

var _excluded = ["messages", "children", "config", "store", "styles", "ToggleIcon", "Button"];
var InterfaceChunk = /*#__PURE__*/React.lazy(function () {
  return import( /* webpackChunkName: "consent-manager-interface" */'./interface-chunk-40aacdb3.esm.js');
});
var ConsentManagerDefaultInterface = function ConsentManagerDefaultInterface(_ref) {
  var _ref$messages = _ref.messages,
    messages = _ref$messages === void 0 ? defaultMessages : _ref$messages,
    children = _ref.children,
    config = _ref.config,
    store = _ref.store,
    _ref$styles = _ref.styles,
    styles = _ref$styles === void 0 ? defaultStyles : _ref$styles,
    _ref$ToggleIcon = _ref.ToggleIcon,
    ToggleIcon = _ref$ToggleIcon === void 0 ? IoShieldCheckmark : _ref$ToggleIcon,
    Button = _ref.Button,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  var _useState = useState(false),
    formVisible = _useState[0],
    setFormVisible = _useState[1];
  // Extend user styles
  styles = _extends({}, defaultStyles, styles);
  var DefaultButton = useDefaultButton(styles);
  var props = _extends({
    styles: styles,
    ToggleIcon: ToggleIcon,
    Button: Button || DefaultButton
  }, rest);
  // Extend user messages by default messages
  messages = _extends({}, defaultMessages, messages);
  return React.createElement(ConsentManager, {
    config: config,
    store: store,
    fallbackComponent: function fallbackComponent(fallbackProps) {
      return React.createElement(FallbackComponent, Object.assign({}, props, fallbackProps, {
        Button: Button || DefaultButton,
        styles: styles,
        ToggleIcon: ToggleIcon
      }));
    }
  }, React.createElement(ConsentManagerDefaultInterfaceContext.Provider, {
    value: {
      formVisible: formVisible,
      setFormVisible: setFormVisible,
      messages: messages
    }
  }, children, React.createElement(Suspense, {
    fallback: null
  }, React.createElement(InterfaceChunk, Object.assign({}, props)))));
};

export { ConsentManagerDefaultInterface as C, IntegrationLabel as I, Trans as T, _objectWithoutPropertiesLoose as _, ConsentManagerDefaultInterfaceContext as a, defaultStyles as b, _createForOfIteratorHelperLoose as c, defaultMessages as d, _extends as e, useDefaultButton as u };
//# sourceMappingURL=index-0df0353c.esm.js.map
